import React, { memo, useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Flame, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import {
    IndexSymbol,
    SUPPORTED_SYMBOLS,
    STORAGE_KEYS,
    type StockQuote,
    type IndexConstituentsSummary
} from '../../config/constants';
import { GeometricAccordion } from '../ui/GeometricAccordion';
import { NumberTicker } from '../ui/NumberTicker';

interface IndexTopMoversSectionProps {
    summaries: Record<IndexSymbol, IndexConstituentsSummary>;
    activeGlobalSymbol: IndexSymbol;
    onSelectIndex?: (sym: IndexSymbol) => void;
}

const RANK_BADGE_COLORS: Record<number, string> = {
    1: 'bg-gradient-to-br from-amber-400/25 to-yellow-500/15 text-amber-700 dark:text-amber-300 border-amber-400/50 dark:border-amber-400/30 shadow-xs',
    2: 'bg-gradient-to-br from-slate-200/60 to-slate-400/20 text-slate-700 dark:text-slate-200 border-slate-300/70 dark:border-slate-600',
    3: 'bg-gradient-to-br from-orange-400/20 to-amber-600/15 text-orange-700 dark:text-orange-300 border-orange-400/50 dark:border-orange-600/40',
    4: 'bg-slate-100 text-slate-600 dark:bg-slate-800/80 dark:text-slate-400 border-slate-200/80 dark:border-slate-700/60',
    5: 'bg-slate-100 text-slate-600 dark:bg-slate-800/80 dark:text-slate-400 border-slate-200/80 dark:border-slate-700/60',
};

const MoverRow = memo(({ quote, rank, type }: { quote: StockQuote; rank: number; type: 'gainer' | 'loser' }) => {
    const isGainer = type === 'gainer';
    const rankStyle = RANK_BADGE_COLORS[rank] || RANK_BADGE_COLORS[4];

    return (
        <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl bg-slate-50/80 dark:bg-slate-800/50 hover:bg-slate-100/90 dark:hover:bg-slate-800 transition-all duration-200 border border-slate-200/60 dark:border-slate-700/50">
            {/* Left: Rank & Symbol */}
            <div className="flex items-center gap-2 min-w-0">
                <span className={`w-5 h-5 flex items-center justify-center rounded-md text-[9.5px] font-black border ${rankStyle} shrink-0`}>
                    #{rank}
                </span>
                <div className="flex flex-col min-w-0">
                    <span className="text-[12px] sm:text-[12.5px] font-black text-slate-800 dark:text-slate-100 tracking-tight leading-tight truncate">
                        {quote.symbol}
                    </span>
                    <span className="text-[9.5px] font-mono text-slate-400 dark:text-slate-500 leading-tight">
                        ₹{quote.ltp.toFixed(2)}
                    </span>
                </div>
            </div>

            {/* Right: Change & % Change */}
            <div className="flex items-center gap-2.5 text-right font-mono shrink-0">
                <span className={`text-[11px] font-bold ${
                    isGainer ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                }`}>
                    {quote.change >= 0 ? '+' : ''}{quote.change.toFixed(2)}
                </span>

                <div className={`flex items-center gap-0.5 text-[10.5px] sm:text-[11px] font-black px-1.5 py-0.5 rounded-md border ${
                    isGainer
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200/80 dark:border-emerald-800/60'
                        : 'bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border-rose-200/80 dark:border-rose-800/60'
                }`}>
                    {isGainer ? (
                        <ArrowUpRight size={12} strokeWidth={3} className="shrink-0" />
                    ) : (
                        <ArrowDownRight size={12} strokeWidth={3} className="shrink-0" />
                    )}
                    <span>
                        {quote.changePercent >= 0 ? '+' : ''}
                        <NumberTicker value={quote.changePercent} decimals={2} />%
                    </span>
                </div>
            </div>
        </div>
    );
});

MoverRow.displayName = 'MoverRow';

export const IndexTopMoversSection: React.FC<IndexTopMoversSectionProps> = memo(({
    summaries,
    activeGlobalSymbol,
    onSelectIndex,
}) => {
    const [selectedTab, setSelectedTab] = useState<IndexSymbol>(activeGlobalSymbol || IndexSymbol.NIFTY);

    // Auto-sync tab when global symbol changes in header
    useEffect(() => {
        if (activeGlobalSymbol && SUPPORTED_SYMBOLS.includes(activeGlobalSymbol)) {
            setSelectedTab(activeGlobalSymbol);
        }
    }, [activeGlobalSymbol]);

    const activeSummary = summaries[selectedTab] || summaries[IndexSymbol.NIFTY];
    const topGainers = activeSummary?.top5Gainers || [];
    const topLosers = activeSummary?.top5Losers || [];

    const handleTabClick = (sym: IndexSymbol) => {
        setSelectedTab(sym);
        onSelectIndex?.(sym);
    };

    return (
        <GeometricAccordion
            title="TOP 5 GAINERS & TOP 5 LOSERS"
            badge={<Flame size={16} />}
            storageKey={STORAGE_KEYS.INDEX_GAINERS_LOSERS_EXPANDED}
            headerRight={
                <div className="flex items-center gap-0.5 bg-slate-100 dark:bg-slate-800/90 p-0.5 rounded-lg border border-slate-200/80 dark:border-slate-700/80 shadow-2xs">
                    {SUPPORTED_SYMBOLS.map((sym) => {
                        const isTabActive = selectedTab === sym;
                        const label = sym === IndexSymbol.BANKNIFTY ? 'BANKNIFTY' : sym;
                        return (
                            <button
                                key={sym}
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleTabClick(sym);
                                }}
                                className={`px-2 py-0.5 rounded-md text-[9.5px] sm:text-[10.5px] font-black transition-all duration-200 cursor-pointer ${
                                    isTabActive
                                        ? 'bg-white dark:bg-indigo-600 text-indigo-700 dark:text-white shadow-xs'
                                        : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                                }`}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            }
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-2.5 lg:gap-3 pt-1.5">
                {/* 🟢 TOP 5 GAINERS (Left Side) */}
                <div className="flex flex-col p-3 rounded-2xl bg-white/95 dark:bg-slate-900/90 border border-emerald-200/60 dark:border-emerald-900/40 shadow-xs">
                    <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-slate-100 dark:border-slate-800/80">
                        <div className="flex items-center gap-1.5">
                            <div className="w-5 h-5 flex items-center justify-center rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                                <TrendingUp size={13} strokeWidth={3} />
                            </div>
                            <span className="text-[11.5px] sm:text-[12px] font-black text-slate-800 dark:text-slate-100 uppercase tracking-tight">
                                Top 5 Gainers ({activeSummary.index === IndexSymbol.NIFTY ? 'NIFTY 50' : activeSummary.displayName})
                            </span>
                        </div>
                        <span className="text-[9.5px] font-extrabold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200/60 dark:border-emerald-800/40 px-2 py-0.5 rounded-full">
                            🟢 Bullish
                        </span>
                    </div>

                    <div className="flex flex-col gap-1">
                        {topGainers.length > 0 ? (
                            topGainers.map((quote, idx) => (
                                <MoverRow key={quote.symbol} quote={quote} rank={idx + 1} type="gainer" />
                            ))
                        ) : (
                            <div className="text-center py-3 text-xs text-slate-400">Loading Gainers...</div>
                        )}
                    </div>
                </div>

                {/* 🔴 TOP 5 LOSERS (Right Side) */}
                <div className="flex flex-col p-3 rounded-2xl bg-white/95 dark:bg-slate-900/90 border border-rose-200/60 dark:border-rose-900/40 shadow-xs">
                    <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-slate-100 dark:border-slate-800/80">
                        <div className="flex items-center gap-1.5">
                            <div className="w-5 h-5 flex items-center justify-center rounded-md bg-rose-500/10 text-rose-600 dark:text-rose-400">
                                <TrendingDown size={13} strokeWidth={3} />
                            </div>
                            <span className="text-[11.5px] sm:text-[12px] font-black text-slate-800 dark:text-slate-100 uppercase tracking-tight">
                                Top 5 Losers ({activeSummary.index === IndexSymbol.NIFTY ? 'NIFTY 50' : activeSummary.displayName})
                            </span>
                        </div>
                        <span className="text-[9.5px] font-extrabold text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 border border-rose-200/60 dark:border-rose-800/40 px-2 py-0.5 rounded-full">
                            🔴 Bearish
                        </span>
                    </div>

                    <div className="flex flex-col gap-1">
                        {topLosers.length > 0 ? (
                            topLosers.map((quote, idx) => (
                                <MoverRow key={quote.symbol} quote={quote} rank={idx + 1} type="loser" />
                            ))
                        ) : (
                            <div className="text-center py-3 text-xs text-slate-400">Loading Losers...</div>
                        )}
                    </div>
                </div>
            </div>
        </GeometricAccordion>
    );
});

IndexTopMoversSection.displayName = 'IndexTopMoversSection';
